import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowDropDown,
  ArrowRight,
  ErrorOutline,
  PlayArrowOutlined,
  Storage,
  Terminal,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import Loading from "../../loading";
import QueryEditor from "../../editorMenu/executeQuery/queryEditor";
import dataBase from "../../../assets/icons/database.svg";
import React, { SetStateAction, useState } from "react";
import useStyles from "./styles";
import { Panel, PanelGroup } from "react-resizable-panels";
import ResizeHandle from "../../resizeHandle";
import { ReactComponent as MySql } from "../../../assets/logosSvg/mySQL_icon.svg";
import { ReactComponent as Postgres } from "../../../assets/logosSvg/postgresql.svg";
import { ReactComponent as MariaDB } from "../../../assets/logosSvg/mariaDB_icon.svg";
import { RowsDataSource } from "../../administracao/types";
import VirtualizedSimpleTreeView from "../../editorMenu/executeQuery/VirtualizedSimpleTreeView";
interface QueryBoxProps {
  errorQuery: string;
  loading: boolean;
  resultQuery: any[];
  dynamicColumns: () => any[];
  customRows: any[];
  filterTable: string;
  setFilterTable: React.Dispatch<SetStateAction<string>>;
  open: boolean;
  setOpen: (open: boolean) => void;
  getFilteredRows: [string, { columnName: string; type: string }[]][];
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  executeQuery: () => void;
  datasources: RowsDataSource[];
  datasource: { id: string } | null;
  setDatasource: React.Dispatch<React.SetStateAction<{ id: string } | null>>;
}

const QueryBox: React.FC<QueryBoxProps> = ({
  errorQuery,
  loading,
  resultQuery,
  dynamicColumns,
  customRows,
  filterTable,
  setFilterTable,
  open,
  setOpen,
  getFilteredRows,
  query,
  setQuery,
  executeQuery,
  datasources,
  datasource,
  setDatasource,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const isDark = theme.palette.mode === "dark";
  const [panelResizeKey, setPanelResizeKey] = useState(0);
  const DB_ICONS: Record<string, React.ReactNode> = {
    MARIADB: (
      <MariaDB
        style={{ width: 22, height: 22, fill: isDark ? "#C0765A" : "#C0765A" }}
      />
    ),
    MYSQL: (
      <MySql
        style={{ width: 22, height: 22, fill: isDark ? "#F29111" : "#F29111" }}
      />
    ),
    POSTGRES: <Postgres style={{ width: 22, height: 22 }} />,
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        height: "400px",
        gap: 1,
      }}
    >
      <PanelGroup direction="horizontal">
        <Panel defaultSize={40}>
          <Box
            sx={{
              border: `solid ${theme.palette.stroke}`,
              borderWidth: "1px 1px 1px 1px",
              backgroundColor: "background.default",
              overflowY: "auto",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                margin: 1,
                justifyContent: "start",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Terminal />
              <Typography component="span">Logs</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                backgroundColor: "background.default",
                padding: 0.2,
              }}
            >
              {errorQuery && (
                <Box
                  sx={{
                    marginLeft: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ErrorOutline
                    sx={{
                      fontSize: "18px",
                      color: theme.palette.error.main,
                      mt: 0.5,
                      mr: 0.5,
                    }}
                  />
                  <Typography sx={{ fontSize: "16px" }}>
                    {errorQuery}
                  </Typography>
                </Box>
              )}
            </Box>
            {loading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <Loading size={120} />
              </Box>
            )}
            {resultQuery && resultQuery.length > 0 && (
              <DataGrid
                columns={dynamicColumns()}
                rows={customRows}
                density="standard"
                disableColumnMenu
                disableColumnFilter
                hideFooter
                localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                sx={styles.dataGrid}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0
                    ? "even-row"
                    : "odd-row"
                }
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
              />
            )}
          </Box>
        </Panel>
        <ResizeHandle isVertical={true} />
        <Panel onResize={() => setPanelResizeKey((k) => k + 1)}>
          <Box
            sx={{
              height: "100%",
              maxWidth: "100%",
              display: "flex",
              border: `solid ${theme.palette.stroke}`,
              borderWidth: "1px 1px 1px 1px",
            }}
          >
            {open && (
              <VirtualizedSimpleTreeView
                rows={getFilteredRows}
                filterTable={filterTable}
                setFilterTable={setFilterTable}
                open={open}
                key={"virtualizedSimpleTreeView-queryFilter"}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingX: 1,
                  paddingY: 0.5,
                  gap: 1.5,
                  width: "100%",
                  height: "fit-content",
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    gap: 2,
                    flexDirection: "row",
                    width: "100%",
                    alignItems: "center",
                    overflowX: "auto",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <IconButton
                      onClick={(e) => setOpen(!open)}
                      size="large"
                      disableRipple
                      disableTouchRipple
                    >
                      <img src={dataBase} alt="ícone banco de dados" />
                    </IconButton>
                    <Typography>SQL</Typography>
                  </Stack>

                  <Autocomplete
                    options={datasources}
                    value={
                      datasources.find(
                        (os) => String(os.id) === String(datasource?.id),
                      ) || null
                    }
                    getOptionLabel={(option) => String(option.name)}
                    onChange={(e, newValue) => {
                      setDatasource(
                        newValue
                          ? {
                              id: newValue.id,
                            }
                          : null,
                      );
                    }}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          py: 1,
                          width: "100%",
                        }}
                      >
                        {DB_ICONS[option.enumType] || (
                          <Postgres style={{ width: 22, height: 22 }} />
                        )}
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    )}
                    renderInput={(params) => {
                      const selectedOption = datasources.find(
                        (os) => String(os.id) === String(datasource?.id),
                      );
                      return (
                        <TextField
                          {...params}
                          placeholder="Selecione o data source"
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              startAdornment: selectedOption && (
                                <Box
                                  sx={{
                                    ml: 1,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {DB_ICONS[selectedOption.enumType]}
                                </Box>
                              ),
                            },
                          }}
                          sx={{
                            width: "100%",
                            minWidth: 200,
                            maxWidth: 240,
                            backgroundColor: theme.palette.background.textField,
                            borderRadius: 1,
                            "& .MuiOutlinedInput-root": {
                              paddingLeft: datasource ? "8px" : "14px", // Ajusta padding quando tem ícone
                              height: "40px", // Altura reduzida conforme a imagem
                              "& fieldset": { borderColor: "transparent" },
                              "&:hover fieldset": {
                                borderColor: "transparent",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                                borderWidth: 1,
                              },
                            },
                          }}
                        />
                      );
                    }}
                  />
                </Box>
                <Button
                  startIcon={<PlayArrowOutlined />}
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.primary.main,
                    borderRadius: "5px",
                  }}
                  onClick={executeQuery}
                  disabled={!query || query.trim() === ""}
                >
                  Run
                </Button>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                }}
              >
                <QueryEditor
                  //   key={panelResizeKey}
                  query={query}
                  setQuery={setQuery}
                  height={"85%"}
                  panelResizeKey={panelResizeKey}
                />
              </Box>
            </Box>
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
};

export default QueryBox;
